import { useState, useEffect, useRef } from "react";

/* ── THEME ─────────────────────────────────────────────────────────────────── */
const T = {
  bg: "#f8fafc", card: "#ffffff", border: "#e2e8f0",
  text: "#0f172a", muted: "#475569", dim: "#94a3b8",
  blue:   { bg: "#dbeafe", text: "#1e40af", border: "#93c5fd" },
  green:  { bg: "#dcfce7", text: "#166534", border: "#86efac" },
  red:    { bg: "#fee2e2", text: "#991b1b", border: "#fca5a5" },
  amber:  { bg: "#fef3c7", text: "#92400e", border: "#fcd34d" },
  purple: { bg: "#ede9fe", text: "#5b21b6", border: "#c4b5fd" },
  teal:   { bg: "#ccfbf1", text: "#0e7490", border: "#5eead4" },
  orange: { bg: "#ffedd5", text: "#9a3412", border: "#fdba74" },
};

/* ── SHARED COMPONENTS ──────────────────────────────────────────────────────── */
const Tag = ({ color = T.blue, children }) => (
  <span style={{ display:"inline-flex", alignItems:"center", padding:"2px 10px", borderRadius:20,
    background:color.bg, color:color.text, fontSize:11, fontWeight:700,
    letterSpacing:"0.05em", textTransform:"uppercase", border:`1px solid ${color.border}` }}>
    {children}
  </span>
);
const Card = ({ children, style={} }) => (
  <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:12,
    padding:"20px 24px", marginBottom:16, boxShadow:"0 1px 3px rgba(0,0,0,0.04)", ...style }}>
    {children}
  </div>
);
const SectionLabel = ({ color=T.blue, children }) => (
  <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
    <div style={{ width:4, height:28, borderRadius:2, background:color.text }} />
    <span style={{ fontFamily:"Georgia,serif", fontSize:13, fontWeight:700,
      color:color.text, letterSpacing:"0.08em", textTransform:"uppercase" }}>
      {children}
    </span>
  </div>
);
const HR = () => <div style={{ height:1, background:T.border, margin:"28px 0" }} />;
function ScoreRing({ score, total, color=T.blue }) {
  const pct = total ? score/total : 0, r=28, circ=2*Math.PI*r;
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
      <svg width={70} height={70}>
        <circle cx={35} cy={35} r={r} fill="none" stroke={T.border} strokeWidth={6}/>
        <circle cx={35} cy={35} r={r} fill="none" stroke={color.text} strokeWidth={6}
          strokeDasharray={circ} strokeDashoffset={circ*(1-pct)} strokeLinecap="round"
          transform="rotate(-90 35 35)" style={{ transition:"stroke-dashoffset 0.6s ease" }}/>
        <text x={35} y={40} textAnchor="middle" fontSize={15} fontWeight={700} fill={T.text}>{score}/{total}</text>
      </svg>
      <span style={{ fontSize:11, color:T.muted, fontWeight:600 }}>{total?Math.round(pct*100):0}%</span>
    </div>
  );
}
function useReveal() {
  const ref=useRef(null); const [visible,setVisible]=useState(false);
  useEffect(()=>{
    const obs=new IntersectionObserver(([e])=>{ if(e.isIntersecting) setVisible(true); },{threshold:0.05});
    if(ref.current) obs.observe(ref.current); return ()=>obs.disconnect();
  },[]);
  return [ref,visible];
}
function Reveal({ children, delay=0 }) {
  const [ref,visible]=useReveal();
  return (
    <div ref={ref} style={{ opacity:visible?1:0, transform:visible?"none":"translateY(18px)",
      transition:`opacity 0.55s ease ${delay}ms, transform 0.55s ease ${delay}ms` }}>
      {children}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   SECTION 1 — 9.1: MONITORING PORTFOLIO PERFORMANCE
══════════════════════════════════════════════════════════════════════════════ */
function Section1() {
  const [trigger, setTrigger] = useState(0);
  const triggers = [
    {
      icon:"📈", label:"Economy / Cycle", tag:"External — Market", color:T.blue,
      headline:"'Is the weather changing outside?' — Monitor macro to keep the portfolio suited to the environment.",
      before:"2021: Client's portfolio was 100% Cyclical stocks (car makers, home builders). Suitable during the boom.",
      after:"2024: RR's monitoring shows 'inverted yield curve' → recession coming. Cyclical stocks CRASH in recessions.",
      action:"'Mr. Smith, I'm concerned your portfolio is too cyclical for the coming recession. I recommend selling your car stock and buying a Consumer Staple (toothpaste) — a Defensive stock that holds up in downturns.'",
    },
    {
      icon:"📉", label:"Interest Rates", tag:"External — Rates", color:T.purple,
      headline:"'The SINGLE most important external factor for Conservative clients.' Rising rates = falling bond prices.",
      before:"2020: 'Conservative' client in Long-Term Bond Fund (high duration). Suitable when rates were zero.",
      after:"2024: Bank of Canada aggressively hiking rates → high-duration bond fund is NOW extremely high-risk. No longer suitable for a Conservative client.",
      action:"'Mrs. Jones, my ongoing monitoring of interest rates has changed my opinion on this fund. The rising rate environment makes it too risky for your Low Risk profile. I recommend we switch to a short-term GIC or Floating Rate Note.'",
    },
    {
      icon:"🏆", label:"Benchmark Performance", tag:"External — KYP", color:T.green,
      headline:"'The report card.' Don't just look at the return — compare it to the benchmark (the yardstick).",
      before:"Client paying 2.2% MER for Active Canadian Growth Fund. Fund TWRR = +8%. Benchmark (TSX) = +12%.",
      after:"Alpha = 8% - 12% = -4%. The client is paying 2.2% per year to LOSE 4% to the 'dumb' index. Total drag: -6.2% vs a cheap ETF.",
      action:"'As part of my ongoing monitoring, this fund has significant negative alpha for 3 quarters in a row. The high cost is not justified. I recommend we sell this and buy a low-cost passive index ETF.'",
    },
    {
      icon:"👤", label:"Client Life Change", tag:"Internal — KYC", color:T.amber,
      headline:"'How has the patient changed?' A life event that invalidates the old KYC is a Material Change.",
      before:"Bill, age 64. KYC: Aggressive Growth, High Risk, 10+ year horizon. Portfolio: 100% Global Tech Fund.",
      after:"Bill calls: 'I'm taking the buyout. Retiring in one month!' New KYC: Income + Capital Preservation, Low-Medium Risk, SHORT horizon.",
      action:"'Bill, this is fantastic news, but it means our ENTIRE plan changes today. Your 100% Tech Fund is now catastrophically unsuitable. My new recommendation is a Conservative Balanced Income portfolio (30% stocks / 70% short-term bonds/GICs).'",
    },
    {
      icon:"🔄", label:"Portfolio Drift", tag:"Internal — Rebalancing", color:T.teal,
      headline:"'The market changed the portfolio for you.' Drift = the portfolio's risk no longer matches the KYC.",
      before:"Client's KYC: Balanced (60/40). After a stock market boom, the portfolio has drifted to 75% stocks / 25% bonds.",
      after:"The portfolio is now Medium-to-High risk — but the client's KYC is only 'Balanced / Medium Risk.' Mismatch = unsuitable.",
      action:"'Your portfolio has drifted to 75/25. This is riskier than your Balanced profile allows. I need to rebalance — sell some stocks, buy some bonds — to restore it to 60/40 and maintain suitability.'",
    },
    {
      icon:"😨", label:"Client Behavioural Reaction", tag:"Internal — Risk Tolerance", color:T.red,
      headline:"'The client's panic is new information.' A strong emotional reaction to a minor market dip reveals true risk tolerance.",
      before:"Client's KYC says 'Conservative.' During a MINOR 5% dip, client calls in a panic: 'I can't sleep! This is too risky!'",
      after:"Revelation: The client's TRUE risk tolerance is LOWER than what's on the KYC form. This is a material fact. Old KYC is invalid.",
      action:"'Your reaction to this temporary dip tells me something important. Your true comfort level is lower than we originally discussed. I need to update your KYC and review whether your current portfolio matches your real risk tolerance.'",
    },
  ];
  const t = triggers[trigger];
  return (
    <Reveal>
      <SectionLabel color={T.blue}>9.1 — Monitoring Portfolio Performance</SectionLabel>
      <Card style={{ background:"linear-gradient(135deg,#eff6ff,#f0fdf4)", border:`1px solid ${T.blue.border}` }}>
        <strong style={{ fontFamily:"Georgia,serif", fontSize:15, color:T.text }}>🩺 The Financial Doctor Analogy</strong>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginTop:10 }}>
          {[
            ["Element 7 (The Prescription)","You diagnose the client (KYC), identify their goals, and write a suitable investment plan."],
            ["Element 9.1 (The Annual Check-Up)","This is the MANDATORY follow-up. Ask TWO questions: (1) How has the PATIENT changed? (2) Is the MEDICINE still working in the current environment?"],
          ].map(([t,d],i)=>(
            <div key={i} style={{ background:T.card, borderRadius:8, padding:"12px 14px", border:`1px solid ${T.border}` }}>
              <div style={{ fontWeight:700, fontSize:12, color:T.blue.text, marginBottom:4 }}>{t}</div>
              <div style={{ fontSize:12, color:T.muted, lineHeight:1.6 }}>{d}</div>
            </div>
          ))}
        </div>
      </Card>
      <strong style={{ fontSize:13, color:T.text, display:"block", marginBottom:10 }}>🚨 The 6 Triggering Events — What Breaks Suitability</strong>
      <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:14 }}>
        {triggers.map((tr,i)=>(
          <button key={i} onClick={()=>setTrigger(i)}
            style={{ padding:"8px 14px", borderRadius:8, border:`2px solid ${trigger===i ? tr.color.text : T.border}`,
              background:trigger===i ? tr.color.bg : T.card, cursor:"pointer", fontSize:12,
              fontWeight:700, color:trigger===i ? tr.color.text : T.muted,
              display:"flex", alignItems:"center", gap:5, transition:"all 0.2s" }}>
            <span>{tr.icon}</span><span>{tr.label}</span>
          </button>
        ))}
      </div>
      <Card style={{ border:`1.5px solid ${t.color.border}`, background:t.color.bg }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
          <span style={{ fontSize:26 }}>{t.icon}</span>
          <div>
            <div style={{ fontFamily:"Georgia,serif", fontWeight:700, fontSize:15, color:T.text }}>{t.label}</div>
            <Tag color={t.color}>{t.tag}</Tag>
          </div>
        </div>
        <p style={{ margin:"0 0 12px", fontSize:13, color:T.text, fontStyle:"italic", lineHeight:1.7 }}>{t.headline}</p>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
          <div style={{ background:T.card, borderRadius:8, padding:"10px 12px", border:`1px solid ${T.green.border}` }}>
            <div style={{ fontSize:11, fontWeight:700, color:T.green.text, marginBottom:4 }}>✓ BEFORE (Was Suitable)</div>
            <div style={{ fontSize:12, color:T.muted, lineHeight:1.5 }}>{t.before}</div>
          </div>
          <div style={{ background:T.card, borderRadius:8, padding:"10px 12px", border:`1px solid ${T.red.border}` }}>
            <div style={{ fontSize:11, fontWeight:700, color:T.red.text, marginBottom:4 }}>✗ AFTER TRIGGER (Now Unsuitable)</div>
            <div style={{ fontSize:12, color:T.muted, lineHeight:1.5 }}>{t.after}</div>
          </div>
        </div>
        <div style={{ background:T.card, borderRadius:8, padding:"10px 14px", border:`1px solid ${t.color.border}` }}>
          <span style={{ fontSize:11, fontWeight:700, color:t.color.text }}>💬 Compliant RR Script: </span>
          <span style={{ fontSize:12, color:T.text, fontStyle:"italic" }}>{t.action}</span>
        </div>
      </Card>
      <Card style={{ border:`1px solid ${T.amber.border}`, background:T.amber.bg }}>
        <strong style={{ color:T.amber.text, fontSize:13 }}>📋 The Annual Review Script — "The Proactive KYC Re-Discovery"</strong>
        <div style={{ marginTop:10, padding:"12px 16px", background:T.card, borderRadius:8, border:`1px solid ${T.border}` }}>
          <p style={{ margin:0, fontSize:12, color:T.text, lineHeight:1.8, fontStyle:"italic" }}>
            "Hi Mrs. Smith, welcome to our annual review. The market part of your portfolio is doing fine, but the MOST IMPORTANT part of this meeting is to re-confirm your KYC — your 'blueprint.'
            <br/>Has anything significant changed in your <strong>personal life</strong>? Marriage, divorce, birth, new dependents?
            <br/>How is your <strong>job</strong>? Any changes to your income or job stability?
            <br/>How is your <strong>health</strong>? Any concerns affecting your long-term plan?
            <br/>Have any of your <strong>goals or time horizons</strong> changed? Still planning to retire at 65?
            <br/>And after last year's market... how are you <strong>feeling about risk</strong>? Is your tolerance still 'Balanced,' or are you feeling more nervous?"
          </p>
          <div style={{ marginTop:10, padding:"8px 12px", background:T.amber.bg, borderRadius:6 }}>
            <span style={{ fontSize:11, fontWeight:700, color:T.amber.text }}>⚡ Rule: </span>
            <span style={{ fontSize:12, color:T.text }}>A 'YES' to ANY of these questions = Material Change → triggers an immediate new Suitability Determination (Element 7.6).</span>
          </div>
        </div>
      </Card>
    </Reveal>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   SECTION 2 — 9.2: PERFORMANCE DISCLOSURE (MWRR vs TWRR, Benchmarks, Peer Groups)
══════════════════════════════════════════════════════════════════════════════ */
function Section2() {
  const [view, setView] = useState("returns");
  return (
    <Reveal delay={60}>
      <SectionLabel color={T.green}>9.2 — Performance Disclosure & Measurement</SectionLabel>
      <Card style={{ background:T.green.bg, border:`1px solid ${T.green.border}` }}>
        <strong style={{ color:T.green.text, fontSize:13 }}>📊 The Report Card Element</strong>
        <p style={{ margin:"6px 0 0", fontSize:12, color:T.text, lineHeight:1.7 }}>
          Clients always ask: <em>"How am I doing?"</em> — But this is actually 4 questions:
          compared to <strong>what benchmark</strong>? compared to <strong>other investors (peer group)</strong>?
          based on <strong>my decisions vs. the manager's</strong>? and what did <strong>costs and taxes</strong> eat?
          The CFRs require you to give a fair, transparent answer to ALL four.
        </p>
      </Card>
      <div style={{ display:"flex", gap:6, marginBottom:14 }}>
        {[["returns","MWRR vs TWRR"],["benchmarks","Benchmarks & Alpha"],["peers","Peer Groups"],["costs","Net-to-Client (Costs & Tax)"]].map(([v,l])=>(
          <button key={v} onClick={()=>setView(v)}
            style={{ padding:"8px 14px", borderRadius:8, border:`1.5px solid ${view===v ? T.green.text : T.border}`,
              background:view===v ? T.green.bg : T.card, cursor:"pointer", fontSize:12,
              fontWeight:700, color:view===v ? T.green.text : T.muted, transition:"all 0.2s" }}>
            {l}
          </button>
        ))}
      </div>

      {view === "returns" && (
        <div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            <Card style={{ border:`1.5px solid ${T.red.border}`, background:T.red.bg }}>
              <div style={{ fontFamily:"Georgia,serif", fontWeight:700, fontSize:14, color:T.text, marginBottom:8 }}>📊 MWRR — Money-Weighted Return</div>
              <Tag color={T.red}>Client's Personal Return</Tag>
              <p style={{ fontSize:12, color:T.text, lineHeight:1.7, margin:"10px 0 8px" }}>
                The number on <strong>the client's account statement</strong>. Heavily influenced by the TIMING of the client's own deposits and withdrawals.
              </p>
              <p style={{ fontSize:12, color:T.muted, fontStyle:"italic", lineHeight:1.6, margin:0 }}>
                <strong>Car Analogy:</strong> The Driver's personal record. If you (the driver) floor it right before a crash, your record will be terrible — even if the car (the fund) is excellent.
              </p>
            </Card>
            <Card style={{ border:`1.5px solid ${T.blue.border}`, background:T.blue.bg }}>
              <div style={{ fontFamily:"Georgia,serif", fontWeight:700, fontSize:14, color:T.text, marginBottom:8 }}>📈 TWRR — Time-Weighted Return</div>
              <Tag color={T.blue}>Manager's Official Report Card</Tag>
              <p style={{ fontSize:12, color:T.text, lineHeight:1.7, margin:"10px 0 8px" }}>
                The <strong>advertised return in Fund Facts</strong> and all marketing. Removes all client cash flows — measures the PURE performance of the portfolio manager's decisions.
              </p>
              <p style={{ fontSize:12, color:T.muted, fontStyle:"italic", lineHeight:1.6, margin:0 }}>
                <strong>Car Analogy:</strong> The car's 0-to-60 time tested by a professional driver on a test track. Completely ignores when or how the client got in or out.
              </p>
            </Card>
          </div>
          <Card style={{ border:`1px solid ${T.amber.border}`, background:T.amber.bg }}>
            <strong style={{ color:T.amber.text, fontSize:13 }}>⚡ Classic Client Complaint — The "Angry Investor" Scenario</strong>
            <div style={{ marginTop:10, display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8 }}>
              {[["Jan 1","Client invests $1,000 in ABC Fund","step"],
                ["Jun 1","Fund up +20%! Client gets excited → invests $100,000 more at the PEAK","bad"],
                ["Dec 31","Fund finishes year at +10% (from Jan 1) but -15% from its June peak","step"],
                ["Client's Statement","MWRR = -8% (bad timing of $100k deposit). Fund TWRR = +10%.","key"],
              ].map(([label,text,type],i)=>(
                <div key={i} style={{ background:T.card, borderRadius:8, padding:"10px 12px", border:`1px solid ${type==="bad"?T.red.border:T.border}` }}>
                  <div style={{ fontSize:10, fontWeight:700, color:T.amber.text, marginBottom:4 }}>{label}</div>
                  <div style={{ fontSize:11, color:T.text, lineHeight:1.5 }}>{text}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop:10, padding:"10px 14px", background:T.card, borderRadius:8, border:`1px solid ${T.border}`, fontSize:12, color:T.text, lineHeight:1.7 }}>
              <strong>RR's Script:</strong> "The 10% is the TWRR — the manager's performance. Your -8% is the MWRR. It's different because you invested $100,000 right at the June peak. The manager DID have a good year — but your timing impacted your personal result."
            </div>
          </Card>
        </div>
      )}

      {view === "benchmarks" && (
        <div>
          <Card style={{ border:`1px solid ${T.blue.border}`, background:T.blue.bg }}>
            <strong style={{ color:T.blue.text, fontSize:13 }}>🎯 Alpha = Fund's Return (TWRR) − Benchmark's Return</strong>
            <p style={{ margin:"8px 0 0", fontSize:12, color:T.text, lineHeight:1.7 }}>
              The benchmark is the "yardstick" — a passive index the active manager is supposed to BEAT.
              Alpha is the measure of whether that manager (and their high 2.2% MER) earned their fee.
            </p>
          </Card>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
            <Card style={{ border:`1px solid ${T.green.border}` }}>
              <strong style={{ fontSize:12, color:T.green.text }}>✓ Positive Alpha — Hold / Suitable</strong>
              <div style={{ marginTop:8, display:"grid", gap:4 }}>
                {[["Fund TWRR","+10%"],["Benchmark (TSX)","+9%"],["Alpha","+1% ✓"],["MER","2.2%"],["Net Alpha","-1.2%"],["Verdict","Borderline — is +1% skill worth 2.2%?"]].map(([k,v])=>(
                  <div key={k} style={{ display:"flex", justifyContent:"space-between", fontSize:12, padding:"3px 0", borderBottom:`1px solid ${T.border}` }}>
                    <span style={{ color:T.muted }}>{k}</span><strong style={{ color:T.text }}>{v}</strong>
                  </div>
                ))}
              </div>
            </Card>
            <Card style={{ border:`1px solid ${T.red.border}` }}>
              <strong style={{ fontSize:12, color:T.red.text }}>✗ Negative Alpha — Sell / Unsuitable</strong>
              <div style={{ marginTop:8, display:"grid", gap:4 }}>
                {[["Fund TWRR","+8%"],["Benchmark (TSX)","+12%"],["Alpha","−4% ✗"],["MER","2.2%"],["Total Drag","−6.2% vs cheap ETF"],["Verdict","Must recommend sell → buy low-cost ETF"]].map(([k,v])=>(
                  <div key={k} style={{ display:"flex", justifyContent:"space-between", fontSize:12, padding:"3px 0", borderBottom:`1px solid ${T.border}` }}>
                    <span style={{ color:T.muted }}>{k}</span><strong style={{ color:T.red.text }}>{v}</strong>
                  </div>
                ))}
              </div>
            </Card>
          </div>
          <strong style={{ fontSize:13, color:T.text, display:"block", marginBottom:10 }}>📏 The SAMURAI Rules — A Compliant Benchmark Must Be:</strong>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:8 }}>
            {[
              ["S — Specified in Advance","Cannot change the test AFTER results. Must be stated before the performance period begins."],
              ["A — Appropriate","Apples-to-apples. Canadian Bond Fund → FTSE Canada Bond Index, NOT a GIC rate."],
              ["M — Measurable","Must be able to calculate its value and total return objectively."],
              ["U — Unambiguous","S&P 500 is unambiguous. 'Global Leaders Fund' is not."],
              ["R — Reflective","Must have the SAME risk and style. Small-Cap fund → Small-Cap Index, not the Large-Cap TSX."],
              ["A — Accountable","The Portfolio Manager — not the client — is accountable for the benchmark."],
              ["I — Investable","The benchmark must be a real alternative. If the fund underperforms, client can fire the manager and just buy an ETF."],
            ].map(([label,text])=>(
              <div key={label} style={{ padding:"10px 12px", borderRadius:8, border:`1px solid ${T.border}`, background:T.card }}>
                <div style={{ fontSize:11, fontWeight:700, color:T.blue.text, marginBottom:4 }}>{label}</div>
                <div style={{ fontSize:11, color:T.muted, lineHeight:1.5 }}>{text}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {view === "peers" && (
        <div>
          <Card style={{ border:`1px solid ${T.purple.border}`, background:T.purple.bg }}>
            <strong style={{ color:T.purple.text, fontSize:13 }}>🏫 Peer Group Analysis = "The Class Average"</strong>
            <p style={{ margin:"8px 0 0", fontSize:12, color:T.text, lineHeight:1.7 }}>
              A Peer Group is all other funds with the same objective (e.g., "Canadian Equity"). Quartile ranking
              (from Morningstar/Fundata) shows how the fund ranks against its direct competitors.
              <strong> Use alongside benchmarks for the FULL picture.</strong>
            </p>
          </Card>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:14 }}>
            {[
              { q:"1st", label:"Top 25%", desc:"The 'A' students. Excellent.", color:T.green },
              { q:"2nd", label:"Top 25–50%", desc:"The 'B' students. Good.", color:T.teal },
              { q:"3rd", label:"Bottom 25–50%", desc:"The 'C' students. Watchlist.", color:T.amber },
              { q:"4th", label:"Bottom 25%", desc:"The 'F' students. Often closed or merged.", color:T.red },
            ].map((p,i)=>(
              <div key={i} style={{ padding:"14px 12px", borderRadius:10, border:`2px solid ${p.color.border}`, background:p.color.bg, textAlign:"center" }}>
                <div style={{ fontFamily:"Georgia,serif", fontSize:22, fontWeight:700, color:p.color.text }}>{p.q}</div>
                <div style={{ fontSize:11, fontWeight:700, color:T.text, margin:"4px 0" }}>{p.label}</div>
                <div style={{ fontSize:11, color:T.muted }}>{p.desc}</div>
              </div>
            ))}
          </div>
          <Card style={{ border:`1px solid ${T.green.border}`, background:T.green.bg }}>
            <strong style={{ color:T.green.text, fontSize:13 }}>✅ Full Analysis Case Study — "The Good Fund"</strong>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginTop:10 }}>
              {[["Fund TWRR","+10%","The fund's return"],["Benchmark (TSX)","+9%","Alpha = +1% ✓"],["Peer Group Avg","+7%","1st Quartile ✓"]].map(([l,v,n])=>(
                <div key={l} style={{ background:T.card, borderRadius:8, padding:"10px 12px", textAlign:"center" }}>
                  <div style={{ fontSize:11, color:T.muted, marginBottom:4 }}>{l}</div>
                  <div style={{ fontFamily:"Georgia,serif", fontSize:20, fontWeight:700, color:T.text }}>{v}</div>
                  <div style={{ fontSize:11, color:T.green.text, marginTop:4 }}>{n}</div>
                </div>
              ))}
            </div>
            <p style={{ margin:"10px 0 0", fontSize:12, color:T.text, lineHeight:1.7, fontStyle:"italic" }}>
              "Our fund beat its benchmark (+1% alpha) AND it crushed its competitors (the average competing fund only made 7%). This fund is in the 1st Quartile. The manager is earning their fee. This is a suitable fund to HOLD."
            </p>
          </Card>
        </div>
      )}

      {view === "costs" && (
        <div>
          <strong style={{ fontSize:13, color:T.text, display:"block", marginBottom:10 }}>🧮 The "Net-to-Client" Filter — What Does the Client ACTUALLY Pocket?</strong>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <Card style={{ border:`1px solid ${T.red.border}` }}>
              <strong style={{ fontSize:12, color:T.red.text }}>⚠️ The MER Drag (Most Common)</strong>
              <div style={{ marginTop:8, display:"grid", gap:4 }}>
                {[["Gross Fund Return (TWRR)","+10.0%"],["Manager's Gross Alpha","+1.0%"],["Fund MER","−2.2%"],["Client's Net Return","+7.8%"],["Benchmark Return","+9.0%"],["Net Impact to Client","−1.2% below benchmark"]].map(([k,v])=>(
                  <div key={k} style={{ display:"flex", justifyContent:"space-between", fontSize:12, padding:"3px 0", borderBottom:`1px solid ${T.border}` }}>
                    <span style={{ color:T.muted }}>{k}</span>
                    <strong style={{ color:v.includes("−")?T.red.text:T.text }}>{v}</strong>
                  </div>
                ))}
              </div>
              <p style={{ margin:"8px 0 0", fontSize:11, color:T.muted, fontStyle:"italic" }}>
                Conclusion: Positive gross alpha (+1%) was EATEN by the 2.2% MER. Not in client's best interest.
              </p>
            </Card>
            <Card style={{ border:`1px solid ${T.teal.border}`, background:T.teal.bg }}>
              <strong style={{ fontSize:12, color:T.teal.text }}>🏦 Tax Implications — After-Tax Analysis</strong>
              <p style={{ fontSize:12, color:T.text, lineHeight:1.6, margin:"6px 0 10px" }}>
                Client: 65-year-old retiree, 40% marginal rate, Non-Registered Account.
                Both options pay a pre-tax 5.0% return.
              </p>
              <div style={{ display:"grid", gap:6 }}>
                {[
                  { label:"Option A — Bond Fund (Interest)", pretax:"5.0%", taxRate:"40%", tax:"−2.0%", net:"3.0%", color:T.red },
                  { label:"Option B — Preferred Share Fund (Dividend)", pretax:"5.0%", taxRate:"~15% (DTC)", tax:"−0.75%", net:"4.25%", color:T.green },
                ].map((o,i)=>(
                  <div key={i} style={{ padding:"10px 12px", borderRadius:8, background:T.card, border:`1px solid ${o.color.border}` }}>
                    <div style={{ fontWeight:700, fontSize:11, color:o.color.text, marginBottom:6 }}>{o.label}</div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:4 }}>
                      {[["Pre-Tax",o.pretax],["Tax Rate",o.taxRate],["Tax",o.tax],["Net Return",o.net]].map(([k,v])=>(
                        <div key={k} style={{ textAlign:"center" }}>
                          <div style={{ fontSize:9, color:T.muted, marginBottom:2 }}>{k}</div>
                          <div style={{ fontSize:12, fontWeight:700, color:k==="Net Return"?o.color.text:T.text }}>{v}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:8, padding:"8px 10px", background:T.card, borderRadius:6, border:`1px solid ${T.green.border}` }}>
                <span style={{ fontSize:11, fontWeight:700, color:T.green.text }}>✓ Compliant Choice: </span>
                <span style={{ fontSize:11, color:T.text }}>Option B delivers 41% higher real return for the same pre-tax yield. The RR MUST perform this tax analysis.</span>
              </div>
            </Card>
          </div>
        </div>
      )}
    </Reveal>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   SECTION 3 — 9.3: PERFORMANCE CALCULATIONS
══════════════════════════════════════════════════════════════════════════════ */
function Section3() {
  const [tool, setTool] = useState("sharpe");
  const [inputs, setInputs] = useState({ ret:10, rfr:2, sd:12, beta:1.0, mktRet:10 });
  const upd = (k,v) => setInputs(p=>({...p,[k]:v}));

  const sharpe = (inputs.ret - inputs.rfr) / (inputs.sd || 1);
  const treynor = (inputs.ret - inputs.rfr) / (inputs.beta || 0.01);
  const expectedCAPM = inputs.rfr + inputs.beta * (inputs.mktRet - inputs.rfr);
  const jensensAlpha = inputs.ret - expectedCAPM;

  const tools = [
    { id:"sharpe", label:"Sharpe Ratio", icon:"📐", color:T.blue },
    { id:"treynor", label:"Treynor Ratio", icon:"📏", color:T.purple },
    { id:"jensen", label:"Jensen's Alpha", icon:"🔬", color:T.green },
    { id:"sd", label:"Standard Deviation", icon:"📊", color:T.amber },
    { id:"mdd", label:"Max Drawdown", icon:"🕳️", color:T.red },
    { id:"multifactor", label:"Multi-Factor Regression", icon:"🧬", color:T.teal },
  ];

  return (
    <Reveal delay={80}>
      <SectionLabel color={T.purple}>9.3 — Performance Calculations</SectionLabel>
      <Card style={{ background:T.purple.bg, border:`1px solid ${T.purple.border}` }}>
        <p style={{ margin:0, fontSize:13, color:T.text, lineHeight:1.7 }}>
          <strong>The Core Question:</strong> "Is the return I got WORTH the risk I took?"
          Anyone can get 20% by taking massive risk. A true professional gets a GOOD return while MANAGING risk.
          These ratios are the KYP "due diligence" tools that let you compare apples-to-apples and identify true skill.
        </p>
      </Card>
      <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:14 }}>
        {tools.map(t=>(
          <button key={t.id} onClick={()=>setTool(t.id)}
            style={{ padding:"8px 14px", borderRadius:8, border:`2px solid ${tool===t.id ? t.color.text : T.border}`,
              background:tool===t.id ? t.color.bg : T.card, cursor:"pointer", fontSize:12,
              fontWeight:700, color:tool===t.id ? t.color.text : T.muted, display:"flex", alignItems:"center", gap:5, transition:"all 0.2s" }}>
            <span>{t.icon}</span><span>{t.label}</span>
          </button>
        ))}
      </div>

      {(tool === "sharpe" || tool === "treynor" || tool === "jensen") && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1.5fr", gap:14 }}>
          <Card>
            <strong style={{ fontSize:13, color:T.text, display:"block", marginBottom:12 }}>🧮 Calculator</strong>
            <div style={{ display:"grid", gap:10 }}>
              {[
                ["Fund Return (%)", "ret"],
                ["Risk-Free Rate (%)", "rfr"],
                ...(tool!=="jensen" ? [["Std Dev (%)", "sd"]] : []),
                ...(tool!=="sharpe" ? [["Fund Beta", "beta"], ["Market Return (%)", "mktRet"]] : []),
              ].map(([label,key])=>(
                <div key={key}>
                  <div style={{ fontSize:11, color:T.muted, marginBottom:3 }}>{label}</div>
                  <input type="number" value={inputs[key]}
                    onChange={e=>upd(key, Number(e.target.value))}
                    style={{ width:"100%", padding:"6px 8px", borderRadius:6, border:`1px solid ${T.border}`,
                      fontSize:12, background:T.bg, color:T.text, boxSizing:"border-box" }} />
                </div>
              ))}
            </div>
          </Card>
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {tool === "sharpe" && (
              <Card style={{ background:T.blue.bg, border:`1px solid ${T.blue.border}` }}>
                <strong style={{ fontSize:13, color:T.blue.text }}>📐 Sharpe Ratio = (Return − Risk-Free Rate) ÷ Std Dev</strong>
                <div style={{ fontSize:28, fontWeight:700, color:T.text, margin:"12px 0", textAlign:"center", fontFamily:"Georgia,serif" }}>
                  {sharpe.toFixed(3)}
                </div>
                <div style={{ fontSize:12, color:T.muted, textAlign:"center" }}>
                  ({inputs.ret}% − {inputs.rfr}%) ÷ {inputs.sd}% = <strong style={{ color:T.blue.text }}>{sharpe.toFixed(3)}</strong>
                </div>
                <HR/>
                <p style={{ margin:0, fontSize:12, color:T.text, lineHeight:1.7 }}>
                  <strong>What it Measures:</strong> Return per unit of TOTAL risk (Standard Deviation).
                  <br/><strong>Higher is ALWAYS better.</strong> A fund returning 10% with 12% SD (Sharpe: 0.67) is MORE efficient than a fund returning 12% with 20% SD (Sharpe: 0.50).
                  <br/><strong>Best for:</strong> Any portfolio comparison — it's the "all-around" efficiency metric.
                </p>
              </Card>
            )}
            {tool === "treynor" && (
              <Card style={{ background:T.purple.bg, border:`1px solid ${T.purple.border}` }}>
                <strong style={{ fontSize:13, color:T.purple.text }}>📏 Treynor Ratio = (Return − Risk-Free Rate) ÷ Beta</strong>
                <div style={{ fontSize:28, fontWeight:700, color:T.text, margin:"12px 0", textAlign:"center", fontFamily:"Georgia,serif" }}>
                  {treynor.toFixed(3)}
                </div>
                <HR/>
                <p style={{ margin:0, fontSize:12, color:T.text, lineHeight:1.7 }}>
                  <strong>What it Measures:</strong> Return per unit of MARKET risk (Beta). Only useful for WELL-DIVERSIFIED portfolios.
                  <br/><strong>Critical Trap:</strong> Completely ignores unsystematic (company-specific) risk. USELESS for a single stock or concentrated portfolio.
                  <br/><strong>Higher is better.</strong> Use only to compare two diversified funds side-by-side.
                </p>
              </Card>
            )}
            {tool === "jensen" && (
              <Card style={{ background:T.green.bg, border:`1px solid ${T.green.border}` }}>
                <strong style={{ fontSize:13, color:T.green.text }}>🔬 Jensen's Alpha = Actual Return − Expected CAPM Return</strong>
                <div style={{ display:"grid", gap:4, marginTop:10, marginBottom:10 }}>
                  {[
                    ["Risk Premium", `${inputs.mktRet}% − ${inputs.rfr}% = ${(inputs.mktRet-inputs.rfr).toFixed(1)}%`],
                    ["Expected Return (CAPM)", `${inputs.rfr}% + ${inputs.beta}×${(inputs.mktRet-inputs.rfr).toFixed(1)}% = ${expectedCAPM.toFixed(2)}%`],
                    ["Actual Return", `${inputs.ret}%`],
                    ["Jensen's Alpha", `${inputs.ret}% − ${expectedCAPM.toFixed(2)}% = ${jensensAlpha.toFixed(2)}%`],
                  ].map(([k,v])=>(
                    <div key={k} style={{ display:"flex", justifyContent:"space-between", fontSize:12, padding:"4px 0", borderBottom:`1px solid ${T.green.border}` }}>
                      <span style={{ color:T.muted }}>{k}</span>
                      <strong style={{ color:T.text, maxWidth:"55%", textAlign:"right" }}>{v}</strong>
                    </div>
                  ))}
                </div>
                <div style={{ padding:"10px 12px", borderRadius:8, background:jensensAlpha>=0?T.green.bg:T.red.bg, border:`1px solid ${jensensAlpha>=0?T.green.border:T.red.border}` }}>
                  <span style={{ fontSize:13, fontWeight:700, color:jensensAlpha>=0?T.green.text:T.red.text }}>
                    {jensensAlpha>=0 ? "✓ Positive Alpha — Manager added skill" : "✗ Negative Alpha — Manager destroyed value"}
                  </span>
                </div>
                <p style={{ margin:"8px 0 0", fontSize:11, color:T.muted }}>
                  Key: This adjusts for the fund's RISK (Beta). A fund with Beta 1.2 SHOULD earn more than the market — Jensen's Alpha shows whether it earned MORE than expected given that risk.
                </p>
              </Card>
            )}
          </div>
        </div>
      )}

      {tool === "sd" && (
        <Card style={{ border:`1px solid ${T.amber.border}`, background:T.amber.bg }}>
          <strong style={{ color:T.amber.text, fontSize:14 }}>📊 Standard Deviation = "Total Risk" / "Bumpiness"</strong>
          <p style={{ fontSize:12, color:T.text, lineHeight:1.7, margin:"8px 0 12px" }}>
            Measures HOW MUCH a fund's returns vary around its average. This is the Fund Facts mandatory risk rating metric (used to assign Low/Low-to-Medium/Medium/etc. ratings).
          </p>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <div style={{ background:T.card, borderRadius:8, padding:"12px 14px", border:`1px solid ${T.green.border}` }}>
              <strong style={{ fontSize:12, color:T.green.text }}>Fund A — Government Bond Fund (Low Risk)</strong>
              <p style={{ fontSize:12, color:T.muted, lineHeight:1.6, margin:"6px 0 0" }}>
                Average return: 20 min commute. SD = <strong>2%</strong>. Always arrives 18–22 min. Stable, predictable. Fund Facts = "Low Risk."
              </p>
            </div>
            <div style={{ background:T.card, borderRadius:8, padding:"12px 14px", border:`1px solid ${T.red.border}` }}>
              <strong style={{ fontSize:12, color:T.red.text }}>Fund B — Tech Sector ETF (High Risk)</strong>
              <p style={{ fontSize:12, color:T.muted, lineHeight:1.6, margin:"6px 0 0" }}>
                Average return: ALSO 20 min commute. SD = <strong>18%</strong>. Could arrive in 5 min or 35 min. Volatile, unpredictable. Fund Facts = "High Risk."
              </p>
            </div>
          </div>
          <div style={{ marginTop:12, padding:"10px 14px", background:T.card, borderRadius:8, border:`1px solid ${T.amber.border}` }}>
            <span style={{ fontSize:11, fontWeight:700, color:T.amber.text }}>📌 Suitability Rule: </span>
            <span style={{ fontSize:12, color:T.text }}>A 70-yr-old "Low-to-Medium Risk" widow: Fund B (SD = 18.5%) is UNSUITABLE. Fund A (SD = 9%) matches her profile. STOP here — no further analysis needed for this client.</span>
          </div>
        </Card>
      )}

      {tool === "mdd" && (
        <Card style={{ border:`1px solid ${T.red.border}`, background:T.red.bg }}>
          <strong style={{ color:T.red.text, fontSize:14 }}>🕳️ Maximum Drawdown (MDD) = "Worst-Case Pain"</strong>
          <p style={{ fontSize:12, color:T.text, lineHeight:1.7, margin:"8px 0 12px" }}>
            The largest peak-to-trough decline in a fund's history. Not "average bumpiness" — it's the WORST crash the client would have survived historically.
          </p>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10 }}>
            {[
              { metric:"Standard Deviation", measures:"Average bumpiness", use:"General volatility comparison", color:T.amber },
              { metric:"Beta", measures:"Market risk sensitivity", use:"Compares market movements", color:T.purple },
              { metric:"Max Drawdown (MDD)", measures:"Worst peak-to-trough loss", use:"'What's the worst crash I'll survive?'", color:T.red },
            ].map((r,i)=>(
              <div key={i} style={{ padding:"12px", borderRadius:8, background:T.card, border:`1.5px solid ${r.color.border}` }}>
                <Tag color={r.color}>{r.metric}</Tag>
                <div style={{ fontSize:11, color:T.muted, marginTop:8, lineHeight:1.5 }}><strong>Measures:</strong> {r.measures}</div>
                <div style={{ fontSize:11, color:T.text, marginTop:4, lineHeight:1.5 }}><strong>Best for:</strong> {r.use}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop:12, padding:"10px 14px", background:T.card, borderRadius:8, border:`1px solid ${T.border}` }}>
            <span style={{ fontSize:11, fontWeight:700, color:T.red.text }}>Client Script: </span>
            <span style={{ fontSize:12, color:T.text, fontStyle:"italic" }}>"I lived through 2008. My ONLY fear is a massive crash. I don't care about average bumpiness — I cannot lose 30%."</span>
            <div style={{ marginTop:6, fontSize:12, color:T.text }}>→ This client is specifically asking you to use MDD as the primary risk metric. SD would answer the wrong question.</div>
          </div>
        </Card>
      )}

      {tool === "multifactor" && (
        <div>
          <Card style={{ border:`1px solid ${T.teal.border}`, background:T.teal.bg }}>
            <strong style={{ color:T.teal.text, fontSize:14 }}>🧬 Multi-Factor Regression = "The DNA Test for Alpha"</strong>
            <p style={{ fontSize:12, color:T.text, lineHeight:1.7, margin:"8px 0 0" }}>
              Deconstructs a fund's return into its components: how much came from market exposure (Beta),
              how much from factor tilts (Value, Small-Cap, Momentum), and what's left over (true Alpha = skill).
              Reveals if a manager is genuinely talented or just "lucky" due to factor exposure.
            </p>
          </Card>
          <Card style={{ border:`1px solid ${T.amber.border}` }}>
            <strong style={{ fontSize:13, color:T.text }}>⚠️ The "Fake Alpha" Case Study</strong>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginTop:12 }}>
              <div>
                <div style={{ fontSize:12, color:T.muted, fontWeight:700, marginBottom:6 }}>What the rookie RR thinks:</div>
                <div style={{ padding:"10px 12px", borderRadius:8, background:T.green.bg, border:`1px solid ${T.green.border}` }}>
                  {[["Fund Return","+12%"],["Benchmark (TSX)","+10%"],["Apparent Alpha","+2% 🎉"],["Verdict","Manager is a GENIUS!"]].map(([k,v])=>(
                    <div key={k} style={{ display:"flex", justifyContent:"space-between", fontSize:12, padding:"3px 0", borderBottom:`1px solid ${T.green.border}` }}>
                      <span style={{ color:T.muted }}>{k}</span><strong style={{ color:T.text }}>{v}</strong>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div style={{ fontSize:12, color:T.muted, fontWeight:700, marginBottom:6 }}>What the multi-factor regression reveals:</div>
                <div style={{ padding:"10px 12px", borderRadius:8, background:T.red.bg, border:`1px solid ${T.red.border}` }}>
                  {[["From Market (Beta)","+9.0%"],["From 'Value' Factor tilt","+4.0%"],["True Manager Alpha","−1.0% ✗"],["Total (checks out)","= 12.0%"]].map(([k,v])=>(
                    <div key={k} style={{ display:"flex", justifyContent:"space-between", fontSize:12, padding:"3px 0", borderBottom:`1px solid ${T.red.border}` }}>
                      <span style={{ color:T.muted }}>{k}</span>
                      <strong style={{ color:k.includes("Alpha")?T.red.text:T.text }}>{v}</strong>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ marginTop:12, padding:"10px 14px", borderRadius:8, background:T.teal.bg, border:`1px solid ${T.teal.border}` }}>
              <span style={{ fontSize:11, fontWeight:700, color:T.teal.text }}>Compliant RR Action: </span>
              <span style={{ fontSize:12, color:T.text, fontStyle:"italic" }}>
                "We need to sell this fund. Our multi-factor analysis shows it was a lucky year for 'Value' stocks. The manager himself actually lost us 1%. We're paying for skill we're not getting. We can get this same Value exposure cheaper with a Value Factor ETF."
              </span>
            </div>
          </Card>
        </div>
      )}
    </Reveal>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   SECTION 4 — 9.4: CIRO COMMUNICATION REQUIREMENTS
══════════════════════════════════════════════════════════════════════════════ */
function Section4() {
  const [view, setView] = useState("core");
  return (
    <Reveal delay={100}>
      <SectionLabel color={T.orange}>9.4 — CIRO Communication Requirements</SectionLabel>
      <Card style={{ background:T.orange.bg, border:`1px solid ${T.orange.border}` }}>
        <strong style={{ color:T.orange.text, fontSize:13 }}>🩺 The "Doctor's Consultation" Analogy</strong>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginTop:8 }}>
          <div style={{ padding:"10px 12px", borderRadius:8, background:T.card, border:`1px solid ${T.red.border}` }}>
            <div style={{ fontSize:11, fontWeight:700, color:T.red.text, marginBottom:4 }}>✗ The Bad Doctor (Violation)</div>
            <div style={{ fontSize:11, color:T.muted, lineHeight:1.5 }}>"Here's a 'miracle pill' — GUARANTEED to make you feel amazing! Don't worry about the fine print, just take it." → A sales pitch, not advice.</div>
          </div>
          <div style={{ padding:"10px 12px", borderRadius:8, background:T.card, border:`1px solid ${T.green.border}` }}>
            <div style={{ fontSize:11, fontWeight:700, color:T.green.text, marginBottom:4 }}>✓ The Good RR (Compliant)</div>
            <div style={{ fontSize:11, color:T.muted, lineHeight:1.5 }}>"Here's what it does (pros), the side effects (risks), the price (cost), the alternatives (other options). Do you understand and agree?" → Fair, balanced, complete.</div>
          </div>
        </div>
      </Card>
      <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:14 }}>
        {[["core","Core Obligations"],["titles","Professional Titles"],["misleading","Misleading Comms"],["social","Social Media"],["offchannel","Off-Channel"]].map(([v,l])=>(
          <button key={v} onClick={()=>setView(v)}
            style={{ padding:"8px 14px", borderRadius:8, border:`1.5px solid ${view===v ? T.orange.text : T.border}`,
              background:view===v ? T.orange.bg : T.card, cursor:"pointer", fontSize:12,
              fontWeight:700, color:view===v ? T.orange.text : T.muted, transition:"all 0.2s" }}>
            {l}
          </button>
        ))}
      </div>

      {view === "core" && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          {[
            { icon:"⚖️", label:"Fair and Balanced", color:T.blue, text:"Risks (cons) must be given the SAME prominence as benefits (pros). You cannot cherry-pick only the good parts. Both sides must be communicated equally." },
            { icon:"🚫", label:"Not Misleading", color:T.red, text:"No lies. No exaggerations. And — critically — no OMISSIONS of key facts. A statement that is technically true but creates a false impression is STILL misleading." },
            { icon:"📝", label:"Plain Language", color:T.green, text:"Use language the SPECIFIC client can understand. Using complex jargon (Beta, Sharpe, Alpha) with a 'Novice' client is a violation — they cannot give 'informed consent' without understanding." },
            { icon:"✅", label:"Suitability Test for Communication", color:T.purple, text:"Before you speak or hit send, ask: 'Is this communication SUITABLE for THIS specific client?' The same message can be compliant for an expert and a violation for a novice." },
          ].map((r,i)=>(
            <Card key={i} style={{ border:`1px solid ${r.color.border}` }}>
              <div style={{ display:"flex", gap:8, alignItems:"flex-start" }}>
                <span style={{ fontSize:22 }}>{r.icon}</span>
                <div>
                  <div style={{ fontWeight:700, fontSize:13, color:T.text, marginBottom:6 }}>{r.label}</div>
                  <div style={{ fontSize:12, color:T.muted, lineHeight:1.6 }}>{r.text}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {view === "titles" && (
        <div>
          <Card style={{ border:`1px solid ${T.blue.border}`, background:T.blue.bg }}>
            <strong style={{ color:T.blue.text, fontSize:13 }}>👤 Rule: You cannot call yourself something you're not.</strong>
            <p style={{ margin:"6px 0 0", fontSize:12, color:T.text, lineHeight:1.7 }}>
              A professional title is a REPRESENTATION of your credentials, experience, and authority.
              Using a fake or exaggerated title is intentionally misleading — it deceives the client into giving you UNEARNED TRUST.
              All titles must be pre-approved by your firm's Compliance department.
            </p>
          </Card>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            {[
              { icon:"📋", violation:"Credential Violation", scenario:"An RR has NOT passed his CFP exams. On his LinkedIn and business card, he calls himself 'John Smith, Financial Planner.'", reason:"He's implying a specific professional credential he has NOT earned. This is deceptive and illegal.", color:T.red },
              { icon:"🏢", violation:"Authority Violation", scenario:"A brand-new, 24-year-old RR (in his first week) prints business cards: 'Jane Doe, Senior Vice President of Wealth Management.'", reason:"She is using a vanity title to gain unearned trust. Her actual title is 'Registered Representative.' Grossly misleading.", color:T.red },
              { icon:"🎓", violation:"Designation Violation (CFA)", scenario:"An RR passed CFA Level 3 but has NOT yet completed the required 4 years of work experience. He writes 'John Smith, CFA' on his business card.", reason:"Passing the exam ≠ earning the charter. Using 'CFA' letters before the Institute grants them is false and misleading.", color:T.red },
              { icon:"✅", violation:"Compliant Title", scenario:"An RR correctly writes 'Jane Doe, Registered Representative' on her business card, pre-approved by Compliance.", reason:"Accurate, not exaggerated, no implied credentials she doesn't hold. Client can trust what they see.", color:T.green },
            ].map((c,i)=>(
              <Card key={i} style={{ border:`1px solid ${c.color.border}`, background:c.color===T.green?T.green.bg:T.card }}>
                <div style={{ display:"flex", gap:8, alignItems:"flex-start" }}>
                  <span style={{ fontSize:20 }}>{c.icon}</span>
                  <div>
                    <Tag color={c.color}>{c.violation}</Tag>
                    <p style={{ margin:"8px 0 4px", fontSize:12, color:T.text, lineHeight:1.6, fontStyle:"italic" }}>{c.scenario}</p>
                    <p style={{ margin:0, fontSize:11, color:T.muted, lineHeight:1.5 }}>→ {c.reason}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {view === "misleading" && (
        <div>
          <Card style={{ border:`1px solid ${T.red.border}`, background:T.red.bg }}>
            <strong style={{ color:T.red.text, fontSize:13 }}>⚠️ Rule: "Don't lie. Don't exaggerate. And don't OMIT important facts."</strong>
            <p style={{ margin:"6px 0 0", fontSize:12, color:T.text, lineHeight:1.7 }}>
              A communication is misleading if it causes a client to form an inaccurate belief — even if it's not a direct lie.
              OMISSION is just as bad as a lie. Fair and balanced = risks get the SAME prominence as rewards.
            </p>
          </Card>
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {[
              { type:"Direct Lie", icon:"🚫", severity:"WORST", color:T.red,
                quote:'"I know GICs are only 5%, but this ABC Stock is GUARANTEED to go up 15%. It\'s a no-lose investment."',
                analysis:"You cannot 'guarantee' the performance of any security. Direct false and misleading statement." },
              { type:"Omission (Cherry-Picking)", icon:"🍒", severity:"Serious", color:T.red,
                quote:"RR creates his own brochure for a Balanced Fund showing ONLY the 2019–2021 returns (+40% total). Intentionally omits the 2022 crash (−40%).",
                analysis:"By omitting the bad years, the RR creates a false impression of true risk. Deceptive act and suitability violation." },
              { type:"Omission (Downplaying Risk)", icon:"🙈", severity:"Serious", color:T.amber,
                quote:"Client asks: 'Is the High-Yield Bond Fund risky?' RR says: 'Don't worry about the risk part. All investments have some risk. Just focus on the 8% income.'",
                analysis:"Actively downplaying material risk (High-Yield = Junk Bonds = high default risk). Must give risks the SAME prominence as the reward." },
              { type:"Complex Jargon with Novice", icon:"📚", severity:"Violation", color:T.purple,
                quote:'"Based on our analysis, this stock\'s Beta is 0.7, Sharpe Ratio is 0.9, and multi-factor regression shows positive Alpha."',
                analysis:"To a 'Low Knowledge' client: this is not a compliant communication. They cannot give 'informed consent' without understanding. Plain language is REQUIRED." },
            ].map((v,i)=>(
              <div key={i} style={{ padding:"12px 16px", borderRadius:10, border:`1.5px solid ${v.color.border}`, background:v.color.bg }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                  <span style={{ fontSize:18 }}>{v.icon}</span>
                  <Tag color={v.color}>{v.type} — {v.severity}</Tag>
                </div>
                <p style={{ margin:"0 0 6px", fontSize:12, color:T.text, fontStyle:"italic", lineHeight:1.6 }}>"{v.quote}"</p>
                <p style={{ margin:0, fontSize:11, color:T.muted, lineHeight:1.5 }}>→ Violation: {v.analysis}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {view === "social" && (
        <div>
          <Card style={{ border:`1px solid ${T.purple.border}`, background:T.purple.bg }}>
            <strong style={{ color:T.purple.text, fontSize:13 }}>📱 Rule: "Your professional rules follow you online."</strong>
            <p style={{ margin:"6px 0 0", fontSize:12, color:T.text, lineHeight:1.7 }}>
              Social media (LinkedIn, Twitter/X, Facebook, TikTok) is instant, permanent, and public.
              ALL communications on these platforms are "communications with the public" and subject to ALL the same CIRO rules.
            </p>
          </Card>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            {[
              { rule:"Pre-Approval Required", icon:"🔐", color:T.blue, ok:"Your firm's Compliance dept must approve all social media profiles and content BEFORE you use them for business.", bad:"Posting business content before Compliance approval." },
              { rule:"No 'Hype' or Promises", icon:"🚀", color:T.red, ok:"'Here are some general educational points about investing in bonds.'", bad:"'My Stock of the Week is TechCo! It's GUARANTEED to pop! 🚀 #investing'" },
              { rule:"No Public Recommendations", icon:"🎯", color:T.red, ok:"Discussing general investment concepts and strategies.", bad:"'ABC Growth Fund is a MUST BUY for anyone saving for retirement!' → MASSIVE violation. Recommending to thousands with NO KYC." },
              { rule:"No Client Testimonials", icon:"⭐", color:T.amber, ok:"Proactively DELETING a client's glowing review (even if true).", bad:"'Liking' or 'Sharing' a client's post: 'Jane made me 50% last year!' → Violates confidentiality + misleadingly implies typical result." },
            ].map((r,i)=>(
              <Card key={i} style={{ border:`1px solid ${r.color.border}` }}>
                <div style={{ display:"flex", gap:6, alignItems:"center", marginBottom:8 }}>
                  <span style={{ fontSize:18 }}>{r.icon}</span>
                  <Tag color={r.color}>{r.rule}</Tag>
                </div>
                <div style={{ fontSize:11, color:T.green.text, marginBottom:4 }}>✓ OK: {r.ok}</div>
                <div style={{ fontSize:11, color:T.red.text }}>✗ Violation: {r.bad}</div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {view === "offchannel" && (
        <div>
          <Card style={{ border:`1px solid ${T.red.border}`, background:T.red.bg }}>
            <strong style={{ color:T.red.text, fontSize:14 }}>📵 NEVER use WhatsApp, iMessage, or personal email for client business. EVER.</strong>
            <p style={{ margin:"8px 0 0", fontSize:12, color:T.text, lineHeight:1.7 }}>
              "Off-channel" = ANY communication about client business NOT on the firm's APPROVED, MONITORED, and RECORDED systems.
              Using off-channel intentionally hides your actions from your supervisor — this is deceptive and unethical.
              If a client complains and you deleted messages, the regulator will ASSUME you are guilty.
            </p>
          </Card>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <Card style={{ border:`1px solid ${T.green.border}`, background:T.green.bg }}>
              <strong style={{ fontSize:12, color:T.green.text }}>✓ ON-CHANNEL (Compliant)</strong>
              <div style={{ marginTop:8 }}>
                {["Your official @megabank.com work email","Your recorded work phone line","Firm-approved instant messaging systems"].map((t,i)=>(
                  <div key={i} style={{ padding:"6px 10px", borderRadius:6, background:T.card, marginBottom:4, fontSize:12, color:T.text }}>✓ {t}</div>
                ))}
              </div>
            </Card>
            <Card style={{ border:`1px solid ${T.red.border}`, background:T.red.bg }}>
              <strong style={{ fontSize:12, color:T.red.text }}>✗ OFF-CHANNEL (Prohibited)</strong>
              <div style={{ marginTop:8 }}>
                {["WhatsApp / Signal / Telegram","Personal Gmail / Hotmail / Yahoo","iMessage from personal cell phone","LinkedIn Direct Messages","Facebook Messenger"].map((t,i)=>(
                  <div key={i} style={{ padding:"6px 10px", borderRadius:6, background:T.card, marginBottom:4, fontSize:12, color:T.text }}>✗ {t}</div>
                ))}
              </div>
            </Card>
          </div>
          <Card style={{ border:`1px solid ${T.amber.border}`, background:T.amber.bg, marginTop:4 }}>
            <strong style={{ color:T.amber.text, fontSize:13 }}>💬 Compliant Response When Client Asks for WhatsApp</strong>
            <p style={{ margin:"8px 0 0", fontSize:12, color:T.text, lineHeight:1.7, fontStyle:"italic" }}>
              "I understand it's more convenient, but I CANNOT do that. My regulatory rules PROHIBIT me from ever discussing clients or trades on an unrecorded system like a personal text.
              This is for YOUR protection — it creates a legal, permanent record of my advice. I must send this to your official email. Please check it when you get off the course."
            </p>
          </Card>
        </div>
      )}
    </Reveal>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   SECTION 5 — 9.5: CLIENT RECORDS
══════════════════════════════════════════════════════════════════════════════ */
function Section5() {
  const [record, setRecord] = useState("kyc");
  const records = [
    { id:"kyc", label:"KYC Records", icon:"🗂️", color:T.blue },
    { id:"account", label:"Account Appropriateness", icon:"🏦", color:T.purple },
    { id:"suitability", label:"Suitability Determination", icon:"📝", color:T.green },
    { id:"kyp", label:"KYP Records (Firm)", icon:"🔍", color:T.teal },
    { id:"coi", label:"Conflict of Interest", icon:"⚠️", color:T.amber },
  ];
  const r = records.find(r=>r.id===record);
  if (!r) return null;
  return (
    <Reveal delay={120}>
      <SectionLabel color={T.teal}>9.5 — Maintain Client Records</SectionLabel>
      <Card style={{ background:"linear-gradient(135deg,#f0fdf4,#fef9c3)", border:`1px solid ${T.green.border}` }}>
        <div style={{ fontFamily:"Georgia,serif", fontSize:18, fontWeight:700, color:T.text, marginBottom:6 }}>
          📜 The Golden Rule: "If it wasn't written down, it didn't happen."
        </div>
        <p style={{ margin:0, fontSize:12, color:T.text, lineHeight:1.7 }}>
          Client notes are NOT paperwork. They are your only legal and regulatory PROOF of competence.
          In a dispute, a client's memory ("You never told me this was risky!") will ALWAYS beat an RR's lack of notes.
          Your records are your <strong>"Suitability Black Box."</strong>
        </p>
      </Card>
      <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:14 }}>
        {records.map(rec=>(
          <button key={rec.id} onClick={()=>setRecord(rec.id)}
            style={{ padding:"8px 14px", borderRadius:8, border:`2px solid ${record===rec.id ? rec.color.text : T.border}`,
              background:record===rec.id ? rec.color.bg : T.card, cursor:"pointer", fontSize:12,
              fontWeight:700, color:record===rec.id ? rec.color.text : T.muted,
              display:"flex", alignItems:"center", gap:5, transition:"all 0.2s" }}>
            <span>{rec.icon}</span><span>{rec.label}</span>
          </button>
        ))}
      </div>

      {record === "kyc" && (
        <div>
          <Card style={{ border:`1px solid ${T.blue.border}`, background:T.blue.bg }}>
            <strong style={{ color:T.blue.text, fontSize:13 }}>🗂️ KYC Records = The Foundation. Every suitability determination is legally anchored to this document.</strong>
            <p style={{ fontSize:12, color:T.text, lineHeight:1.7, margin:"8px 0 0" }}>
              The New Account Application Form (NAAF) PLUS all contemporaneous notes and emails used to gather KYC.
              The "blueprint" of your client. The signed form alone is NOT enough — you need the note-to-file explaining WHY the KYC is what it is.
            </p>
          </Card>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <Card style={{ border:`1px solid ${T.red.border}` }}>
              <strong style={{ fontSize:12, color:T.red.text }}>✗ "The Checkbox RR" — Worthless Record</strong>
              <p style={{ fontSize:12, color:T.muted, lineHeight:1.6, margin:"8px 0 0", fontStyle:"italic" }}>
                RR sits with new client, checks boxes: "Income: $50-100k, Risk: Medium." Gets signature at bottom.
              </p>
              <p style={{ fontSize:11, color:T.red.text, margin:"8px 0 0", lineHeight:1.5 }}>
                Client later claims: "The RR just filled that out himself and told me where to sign. We never had a real conversation about risk." RR has NO defense.
              </p>
            </Card>
            <Card style={{ border:`1px solid ${T.green.border}`, background:T.green.bg }}>
              <strong style={{ fontSize:12, color:T.green.text }}>✓ "The Professional RR" — Perfect Record</strong>
              <div style={{ marginTop:8, padding:"10px 12px", background:T.card, borderRadius:8, border:`1px solid ${T.border}`, fontSize:11, color:T.text, lineHeight:1.8 }}>
                "Date: Nov 10, 2025. 90-min KYC meeting with Jane Smith.
                <br/><strong>Risk Profile:</strong> Jane stated 'Aggressive' (age 35). My ANALYSIS of her Capacity shows $50k for 2-yr house down payment.
                <br/><strong>Resolution:</strong> Discussed conflict. Explained her 2-yr horizon VETOES the 'Aggressive' tolerance for that goal.
                <br/><strong>Outcome:</strong> Agreed overall profile is 'Balanced,' but $50k house portion = 'Low Risk.' She understood and confirmed."
              </div>
            </Card>
          </div>
        </div>
      )}

      {record === "account" && (
        <div>
          <Card style={{ border:`1px solid ${T.purple.border}`, background:T.purple.bg }}>
            <strong style={{ color:T.purple.text, fontSize:13 }}>🏦 Account Appropriateness = Prove WHY this account type is in the client's best interest.</strong>
            <p style={{ fontSize:12, color:T.text, lineHeight:1.7, margin:"8px 0 0" }}>
              Primary defense against "Reverse Churning" — putting a buy-and-hold GIC client in a 1.5%/year fee-based account.
              Must prove you chose the account for the client's benefit, not your own.
            </p>
          </Card>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <Card style={{ border:`1px solid ${T.red.border}` }}>
              <strong style={{ fontSize:12, color:T.red.text }}>✗ The Violation Scenario</strong>
              <p style={{ fontSize:12, color:T.muted, lineHeight:1.6, margin:"8px 0", fontStyle:"italic" }}>
                75-year-old widow. Low Risk. Only ever buys GICs.
                RR puts her in a Fee-Based Account at 1.5%/year = $15,000/year fee just to hold a GIC.
                Client's daughter (with POA) complains.
              </p>
              <p style={{ fontSize:11, color:T.red.text, lineHeight:1.5, margin:0 }}>
                The RR cannot produce ANY record justifying why a high-cost fee-based account is suitable for a buy-and-hold client who never trades.
              </p>
            </Card>
            <Card style={{ border:`1px solid ${T.green.border}`, background:T.green.bg }}>
              <strong style={{ fontSize:12, color:T.green.text }}>✓ A Compliant Account Appropriateness Note</strong>
              <div style={{ marginTop:8, padding:"10px 12px", background:T.card, borderRadius:8, border:`1px solid ${T.border}`, fontSize:11, color:T.text, lineHeight:1.8 }}>
                "Date: Nov 10. Client is a 'Balanced' investor.
                <br/><strong>Analysis:</strong> Account requires active quarterly rebalancing to maintain 60/40 SAA.
                <br/><strong>Cost Comparison:</strong> Explained 4-5 annual rebalancing trades = likely more expensive than 1.25% annual fee-based account.
                <br/><strong>Outcome:</strong> Client agreed fee-based account is more suitable and cost-effective for his active strategy. Consented to account type."
              </div>
            </Card>
          </div>
        </div>
      )}

      {record === "suitability" && (
        <div>
          <Card style={{ border:`1px solid ${T.green.border}`, background:T.green.bg }}>
            <strong style={{ color:T.green.text, fontSize:13 }}>📝 The Suitability Determination Record = "The Master Note." Proof for every single investment action.</strong>
            <p style={{ fontSize:12, color:T.text, lineHeight:1.7, margin:"8px 0 0" }}>
              Required for every buy, sell, hold, or exchange. Must prove: (1) WHAT you recommended, (2) WHY you recommended it,
              (3) what the RISKS were, (4) that the client AGREED. Your ONLY defense in a suitability complaint.
            </p>
          </Card>
          <Card style={{ border:`1px solid ${T.green.border}` }}>
            <strong style={{ fontSize:13, color:T.text }}>📄 A Perfect "Buy" Note Template</strong>
            <div style={{ marginTop:10 }}>
              {[
                { step:"KYC Check", icon:"1️⃣", text:"Confirmed Jane's KYC unchanged: 'Balanced' (60/40), 'Medium Risk,' 20-year horizon." },
                { step:"Action & Accomplishment", icon:"2️⃣", text:"Recommended $10,000 of 'ABC Global Balanced Fund (Series F).' This accomplishes her Growth and Diversification objectives — a one-ticket 60/40 global fund." },
                { step:"Risks, Pros & Cons", icon:"3️⃣", text:"Discussed risks: as a 'Balanced' fund, it still holds 60% stocks and WILL fall in a market crash. Not a GIC. Discussed pro (simplicity) and con (1.2% MER)." },
                { step:"Alternatives Considered", icon:"4️⃣", text:"Compared to buying four separate ETFs. Agreed this one wrap fund is more suitable for her hands-off preference." },
                { step:"Informed Consent", icon:"5️⃣", text:"Client confirmed she understood the risks, costs, and alternatives, and gave her informed consent to proceed." },
              ].map((s,i)=>(
                <div key={i} style={{ display:"flex", gap:10, padding:"8px 0", borderBottom:`1px solid ${T.border}` }}>
                  <span style={{ fontSize:16, flexShrink:0 }}>{s.icon}</span>
                  <div>
                    <div style={{ fontSize:12, fontWeight:700, color:T.green.text, marginBottom:2 }}>{s.step}</div>
                    <div style={{ fontSize:12, color:T.muted, lineHeight:1.5 }}>{s.text}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {record === "kyp" && (
        <div>
          <Card style={{ border:`1px solid ${T.teal.border}`, background:T.teal.bg }}>
            <strong style={{ color:T.teal.text, fontSize:13 }}>🔍 KYP Records = The FIRM's product due diligence file. Not the RR's — the dealer's.</strong>
            <p style={{ fontSize:12, color:T.text, lineHeight:1.7, margin:"8px 0 0" }}>
              Before ANY product can be sold by any RR, the firm's "New Product Committee" must analyze and approve it.
              This creates the firm's defense of non-negligence. A solid KYP file can shift liability from the firm to the individual RR who ignored internal controls.
            </p>
          </Card>
          <Card>
            <strong style={{ fontSize:13, color:T.text }}>Case Study: Private Mortgage Fund Gets "Gated" in a Crisis</strong>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginTop:10 }}>
              <div>
                <div style={{ fontSize:12, fontWeight:700, color:T.red.text, marginBottom:6 }}>Without a KYP file:</div>
                <div style={{ padding:"10px 12px", borderRadius:8, background:T.red.bg, border:`1px solid ${T.red.border}`, fontSize:12, color:T.text, lineHeight:1.6 }}>
                  Clients complain: "The firm sold us a risky, illiquid product." Firm has no defense. BOTH the firm AND every individual RR are liable.
                </div>
              </div>
              <div>
                <div style={{ fontSize:12, fontWeight:700, color:T.green.text, marginBottom:6 }}>With a complete KYP file:</div>
                <div style={{ padding:"10px 12px", borderRadius:8, background:T.green.bg, border:`1px solid ${T.green.border}`, fontSize:12, color:T.text, lineHeight:1.6 }}>
                  Firm shows: liquidity risk analysis, approval memo with risk flagged, AND internal memo to all RRs: "APPROVED but HIGH RISK and ILLIQUID — only for Accredited Investors with NO liquidity need."
                  → Liability shifts to the RR who ignored the internal control.
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {record === "coi" && (
        <div>
          <Card style={{ border:`1px solid ${T.amber.border}`, background:T.amber.bg }}>
            <strong style={{ color:T.amber.text, fontSize:13 }}>⚠️ COI Records = Proof that a conflict was identified, disclosed, and STILL resulted in a best-interest recommendation.</strong>
            <p style={{ fontSize:12, color:T.text, lineHeight:1.7, margin:"8px 0 0" }}>
              Without a COI record, an RR who recommends proprietary (in-house) funds is PRESUMED GUILTY of violating the best-interest duty.
              The CFRs require you to document that you considered a reasonable range of alternatives.
            </p>
          </Card>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1.2fr", gap:12 }}>
            <Card style={{ border:`1px solid ${T.red.border}` }}>
              <strong style={{ fontSize:12, color:T.red.text }}>✗ The Violation Record</strong>
              <div style={{ marginTop:8, padding:"10px 12px", background:T.red.bg, borderRadius:8, border:`1px solid ${T.red.border}`, fontSize:12, color:T.text, fontStyle:"italic", lineHeight:1.6 }}>
                "Recommended $50k of MegaBank Active Fund. Client agreed."
              </div>
              <p style={{ fontSize:11, color:T.red.text, margin:"8px 0 0", lineHeight:1.5 }}>
                No proof the conflict was identified. No proof alternatives were considered. No proof costs were disclosed. Indefensible against a complaint.
              </p>
            </Card>
            <Card style={{ border:`1px solid ${T.green.border}`, background:T.green.bg }}>
              <strong style={{ fontSize:12, color:T.green.text }}>✓ The Compliant "CFR Note"</strong>
              <div style={{ marginTop:8, padding:"10px 12px", background:T.card, borderRadius:8, border:`1px solid ${T.border}`, fontSize:11, color:T.text, lineHeight:1.8 }}>
                "Date: Nov 10. Recommend $50k 'MegaBank Active Fund' (PROPRIETARY product).
                <br/><strong>Alternatives Discussed:</strong> Compared to Passive Global ETF (0.2% MER). Disclosed 2.0% cost difference.
                <br/><strong>Rationale (Why Best Interest):</strong> KYP shows manager has 10-yr track record of +3.5% alpha annually. Fund also has lower Max Drawdown.
                <br/><strong>Conflict Disclosure:</strong> Verbally disclosed this is a proprietary in-house fund and firm benefits.
                <br/><strong>Client Decision:</strong> Client agreed potential alpha and downside protection worth the higher cost and conflict."
              </div>
            </Card>
          </div>
        </div>
      )}
    </Reveal>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   FLASHCARDS
══════════════════════════════════════════════════════════════════════════════ */
const FLASHCARDS = [
  { q:"What are the TWO questions every annual portfolio review must answer?", a:"(1) How has the PATIENT changed? (Has the client's life, goals, or risk tolerance changed?) (2) Is the MEDICINE still working? (Has the market/economy changed to make the portfolio unsuitable?)" },
  { q:"Name 3 external 'triggering events' that can break suitability.", a:"(1) Economic Cycle change (boom → recession → Cyclical stocks become unsuitable). (2) Interest Rate rise (long-duration bonds become High Risk for Conservative clients). (3) Fund underperforming its Benchmark for multiple periods (negative Alpha → must recommend selling)." },
  { q:"Name 3 internal/client 'triggering events' that invalidate old KYC.", a:"(1) Major life event (retirement, job loss, death of spouse). (2) Portfolio drift (60/40 drifts to 75/25 due to market rally → now too risky). (3) Client panic at minor dip (reveals true Risk Tolerance is lower than KYC says)." },
  { q:"What is MWRR and where does it appear?", a:"Money-Weighted Rate of Return — the client's PERSONAL return, heavily influenced by the timing of THEIR OWN cash deposits and withdrawals. Appears on the client's account statement. A bad investor (bad timing) will have a terrible MWRR even in a great fund." },
  { q:"What is TWRR and where does it appear?", a:"Time-Weighted Rate of Return — the fund MANAGER'S official return. Removes the effect of all client cash flows. Appears in Fund Facts, marketing materials, and all official performance reporting. The standard for comparing funds and evaluating manager skill." },
  { q:"Classic exam trap: Fund TWRR = +10% but client's MWRR = −5%. Explain.", a:"The client invested a large sum RIGHT AT THE MARKET PEAK. The fund manager had a good year (+10%), but the client's TIMING of their large deposit drove their personal result negative. MWRR is distorted by cash flow timing; TWRR is not." },
  { q:"Alpha formula and what a negative Alpha means.", a:"Alpha = Fund's Return (TWRR) − Benchmark's Return. A NEGATIVE Alpha means the fund underperformed the 'dumb' passive index BEFORE fees. After a high MER (e.g., 2.2%), the total drag can be enormous. Ongoing monitoring duty: must recommend selling the fund." },
  { q:"What does the SAMURAI acronym stand for for benchmarks?", a:"Specified in Advance, Appropriate (apples-to-apples), Measurable, Unambiguous, Reflective (same risk/style), Accountable, Investable. A Canadian Small-Cap fund must be benchmarked to the Small-Cap Index, NOT the large-cap S&P/TSX Composite." },
  { q:"What is Peer Group Analysis (Quartile Ranking)?", a:"Comparing a fund's return to the average of all OTHER funds with the same objective (e.g., Canadian Equity). 1st Quartile = top 25% (A students). 4th Quartile = bottom 25% (F students, often closed). Used alongside benchmark analysis for a complete picture." },
  { q:"Sharpe Ratio — formula and interpretation.", a:"Sharpe = (Fund Return − Risk-Free Rate) ÷ Standard Deviation. Measures return per unit of TOTAL risk. HIGHER is always better. A fund returning 10% with SD of 12% (Sharpe: 0.67) is MORE efficient than 12% with SD of 20% (Sharpe: 0.50). Best all-around metric." },
  { q:"Treynor Ratio — formula, key limitation.", a:"Treynor = (Fund Return − Risk-Free Rate) ÷ Beta. Measures return per unit of MARKET risk (Beta). KEY LIMITATION: Only useful for well-DIVERSIFIED portfolios — ignores unsystematic risk. USELESS for a single stock or concentrated portfolio." },
  { q:"Jensen's Alpha — how is it different from simple Alpha?", a:"Jensen's Alpha = Actual Return − Expected Return (CAPM). It adjusts for the fund's risk (Beta). A fund with Beta 1.2 SHOULD earn more than the market — Jensen's Alpha shows if it earned more than its EXPECTED return given that risk level. More precise than simple Alpha." },
  { q:"Standard Deviation vs. Maximum Drawdown — which for which client?", a:"SD = average bumpiness (for general risk rating/Fund Facts). Max Drawdown (MDD) = worst peak-to-trough historical loss. Use MDD when a client specifically says: 'I don't care about average volatility — I cannot survive a major crash.' MDD answers the 'worst-case pain' question." },
  { q:"What does Multi-Factor Regression reveal that simple Alpha does not?", a:"It deconstructs a fund's return: how much came from Beta (market), how much from factor tilts (Value, Small-Cap), and what's TRULY left over (pure Alpha = skill). Can reveal 'Fake Alpha' — a manager who APPEARED to beat the market was just lucky due to factor exposure, not skill." },
  { q:"CIRO 3-part communication obligation (Fair, Not Misleading, Clear).", a:"(1) FAIR and BALANCED: risks must get SAME prominence as benefits — cannot cherry-pick the good. (2) NOT MISLEADING: no lies, exaggerations, OR omissions of key facts. (3) CLEAR: use PLAIN LANGUAGE appropriate for the specific client's knowledge level — jargon with a novice is a violation." },
  { q:"What is an 'off-channel communication' and why is it catastrophic?", a:"ANY business communication NOT on the firm's approved, monitored, recorded systems (e.g., WhatsApp, iMessage, personal Gmail). Catastrophic: (1) Bypasses firm's mandatory supervision. (2) Destroys the official record — if messages are deleted and a client complains, the regulator assumes guilt." },
  { q:"What are the 4 Social Media rules for RRs?", a:"(1) Pre-approval from Compliance before using social media for business. (2) No 'hype,' promises, or guarantees. (3) No public recommendations — you have zero KYC on your followers. (4) No client testimonials — even 'liking' one violates confidentiality and creates a misleadingly favorable impression." },
  { q:"Name the 5 main types of records an RR must maintain.", a:"(1) KYC Records (NAAF + note-to-file explaining WHY). (2) Account Appropriateness record (why this account type). (3) Suitability Determination record (the 'Master Note' for every trade). (4) KYP records (the FIRM's product due diligence). (5) COI records (conflicts identified, alternatives considered, best-interest justification)." },
  { q:"Why is the 'Golden Rule' ('if it wasn't written down, it didn't happen') so important?", a:"In a dispute, a client's memory ('You never told me this was risky!') ALWAYS beats an RR's lack of notes. Without a contemporaneous record, the RR has no proof the suitability conversation occurred. The record IS the evidence — without it, there is no defense, regardless of how compliant the verbal advice was." },
  { q:"What must a compliant COI/Suitability note include when recommending a proprietary fund?", a:"Must document: (1) The conflict identified (proprietary/in-house product). (2) Alternatives considered (e.g., low-cost ETF). (3) Cost impact disclosed (e.g., 2.0% MER difference). (4) Specific RATIONAL for why the conflicted option is still best interest (e.g., manager's 10-yr alpha track record). (5) Client's informed consent." },
];

function Flashcards() {
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [mastered, setMastered] = useState(new Set());
  const remaining = FLASHCARDS.filter((_,i)=>!mastered.has(i));
  function next(master) {
    if(master) setMastered(s=>new Set([...s, idx]));
    const available = FLASHCARDS.map((_,i)=>i).filter(i=>!mastered.has(i)&&(master?i!==idx:true));
    if(available.length===0) setMastered(new Set());
    else setIdx(available[Math.floor(Math.random()*available.length)]);
    setFlipped(false);
  }
  if(remaining.length===0) return (
    <Card style={{ textAlign:"center", padding:32 }}>
      <div style={{ fontSize:40, marginBottom:8 }}>🏆</div>
      <div style={{ fontFamily:"Georgia,serif", fontSize:18, color:T.text, marginBottom:8 }}>All 20 cards mastered!</div>
      <button onClick={()=>{ setMastered(new Set()); setIdx(0); setFlipped(false); }}
        style={{ padding:"10px 24px", background:T.blue.text, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700 }}>Restart</button>
    </Card>
  );
  const card = FLASHCARDS[idx];
  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <Tag color={T.blue}>{remaining.length} remaining</Tag>
        <Tag color={T.green}>{mastered.size} mastered</Tag>
      </div>
      <div onClick={()=>setFlipped(!flipped)} style={{ cursor:"pointer" }}>
        <div style={{ minHeight:160, borderRadius:12, border:`2px solid ${flipped?T.green.border:T.blue.border}`,
          background:flipped?T.green.bg:T.blue.bg, padding:"24px 28px", display:"flex", alignItems:"center",
          justifyContent:"center", transition:"all 0.3s", position:"relative" }}>
          <div style={{ position:"absolute", top:12, right:14, fontSize:11, color:T.muted, fontWeight:600 }}>
            {flipped?"ANSWER":"QUESTION — tap to flip"}
          </div>
          <p style={{ margin:0, fontSize:13, lineHeight:1.75, textAlign:"center", color:T.text,
            fontFamily:flipped?"system-ui":"Georgia,serif", fontWeight:flipped?400:600 }}>
            {flipped ? card.a : card.q}
          </p>
        </div>
      </div>
      {flipped && (
        <div style={{ display:"flex", gap:10, marginTop:12 }}>
          <button onClick={()=>next(false)} style={{ flex:1, padding:10, borderRadius:8, border:`1.5px solid ${T.red.border}`,
            background:T.red.bg, color:T.red.text, cursor:"pointer", fontWeight:700, fontSize:13 }}>✗ Still Learning</button>
          <button onClick={()=>next(true)} style={{ flex:1, padding:10, borderRadius:8, border:`1.5px solid ${T.green.border}`,
            background:T.green.bg, color:T.green.text, cursor:"pointer", fontWeight:700, fontSize:13 }}>✓ Got It</button>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   QUIZ — 7 QUESTIONS
══════════════════════════════════════════════════════════════════════════════ */
const QUESTIONS = [
  {
    q:"A client's portfolio was 100% in a Long-Term Bond Fund when rates were zero. The Bank of Canada has now aggressively raised rates for 18 months. What is the RR's PRIMARY ongoing suitability duty?",
    opts:["Do nothing — the portfolio was suitable when purchased, so it remains suitable","The RR must recognize that the rate environment has made this high-duration fund unsuitable for a Conservative client, and must call to recommend switching to a short-term GIC or Floating Rate Note","The RR must wait for the client's KYC to change before taking any action","The RR must buy more of the bond fund as yields are now higher"],
    ans:1, explain:"Monitoring the external environment is a core ongoing duty. A rising rate environment makes high-duration bonds HIGH RISK — this is a macro-triggered suitability failure. The RR must act proactively. Waiting for a client-triggered KYC change is wrong."
  },
  {
    q:"A fund's website shows a +10% annual return. A client's account statement shows their personal return is −5%. The client calls furious: 'You're lying to me!' What is the BEST explanation?",
    opts:["The 10% return is before fees; the −5% is after fees","The 10% is the TWRR (the manager's performance, removing cash flow effects). The client's −5% is the MWRR — negatively impacted by the timing of their large deposit right before the market pulled back","The client is in the wrong share class (A-Class vs. F-Class)","The 10% return is the MWRR, which is the firm's reported return"],
    ans:1, explain:"Classic TWRR vs. MWRR conflict. TWRR (advertised) removes the impact of cash flows and measures manager skill. MWRR (personal statement) is heavily distorted by the TIMING of the client's own deposits — investing a large sum at the peak leads to a terrible personal result even in a good fund."
  },
  {
    q:"An Active Canadian Growth Fund (MER 2.2%) has a 3-year TWRR of +8%. The S&P/TSX Composite Total Return Index returned +12% over the same period. What is the fund's Alpha and the most suitable action?",
    opts:["Alpha is +8%. The fund had a positive return. Hold.","Alpha is −4%. The client is paying a high fee for a manager who significantly underperformed the cheap index. The RR must recommend selling this fund and buying a low-cost passive index ETF","Alpha is +2%. The fund is performing above expectations. Buy more.","Alpha is −4%. This is a buy signal — the fund is due to revert to the mean"],
    ans:1, explain:"Alpha = Fund Return − Benchmark = 8% − 12% = −4%. Negative alpha is a clear monitoring failure trigger. Combined with the 2.2% MER, the client is paying to significantly underperform a low-cost alternative. The ongoing suitability duty REQUIRES the RR to act — not hold and hope."
  },
  {
    q:"An RR is comparing two 'Balanced' funds for a Medium Risk client (Risk-Free Rate = 2%). Fund A: Return = 12%, SD = 20%. Fund B: Return = 10%, SD = 12%. Which fund does the Sharpe Ratio say is MORE suitable?",
    opts:["Fund A — its 12% return is higher, making it more efficient","Fund B — its Sharpe Ratio (0.67) is higher than Fund A's (0.50), proving it provides more return per unit of risk taken","Both are equal, as both have positive returns","Fund A — its higher volatility indicates more upside potential"],
    ans:1, explain:"Sharpe(A) = (12−2)/20 = 0.50. Sharpe(B) = (10−2)/12 = 0.67. Fund B is MORE efficient — it rewarded the client MORE per unit of risk. A 'Balanced / Medium Risk' client should NOT accept Fund A's extra 20% volatility just for 2% more return. The Sharpe Ratio reveals this clearly."
  },
  {
    q:"An RR posts on his public LinkedIn page: 'Just reviewed the ABC Global Tech Fund — up 45% last year! This is a MUST BUY for ANYONE saving for retirement!' What is the PRIMARY violation?",
    opts:["It is not a violation as the 45% return is a fact","This is a blanket public recommendation to thousands of people with NO KYC information — an indiscriminate, potentially unsuitable recommendation","It is an off-channel communication violation as LinkedIn is not approved","It violates the pre-approval requirement for social media"],
    ans:1, explain:"The BIGGEST social media risk. An RR CANNOT make a public recommendation. Recommendations MUST be personalized based on a specific client's KYC. A 'High Risk' Tech Fund could be catastrophically unsuitable for a Low Risk retiree who reads this post. This is the core reason public social media recommendations are prohibited."
  },
  {
    q:"A busy client asks his RR to text investment recommendations to his personal iPhone via iMessage. The RR agrees and texts a recommendation. The stock crashes to $0. CIRO investigates. What is the most serious 'off-channel' violation?",
    opts:["The RR used an iPhone instead of an Android device","The RR intentionally bypassed the firm's mandatory monitoring and archiving systems, creating no legal record and hiding his actions from supervisors","The RR failed to get the client to sign a written consent for texting","The RR failed to use plain language in the text message"],
    ans:1, explain:"Off-channel communication is catastrophic because: (1) It BYPASSES the firm's mandatory supervision (the firm could not catch a misleading recommendation). (2) It DESTROYS the official record — deleted messages = assumed guilt. The firm's legal obligation to supervise and archive ALL client communications is a cornerstone of client protection."
  },
  {
    q:"An RR verbally gives a client a compliant 20-minute recommendation covering all risks, costs, and alternatives. The client agrees. The RR places the trade but forgets to write a client note. Two years later the client claims 'You never told me this was risky.' What is the RR's position?",
    opts:["Protected — the verbal conversation was compliant, so the RR is covered","In serious trouble — without a contemporaneous suitability determination record, in the eyes of the regulator 'if it wasn't written down, it didn't happen.' The RR cannot defend against the complaint","Protected — the trade confirmation serves as the record of the recommendation","Partially protected — the client's signature on the NAAF is sufficient"],
    ans:1, explain:"The Golden Rule of compliance: 'If it wasn't written down, it didn't happen.' No matter how compliant the verbal advice was, without a contemporaneous note-to-file the RR has ZERO proof the suitability conversation occurred. The client's memory will beat the RR's silence in every regulatory proceeding."
  },
];

function Quiz() {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [current, setCurrent] = useState(0);
  const score = Object.entries(answers).filter(([i,a])=>QUESTIONS[+i].ans===a).length;
  if(submitted) return (
    <div>
      <div style={{ display:"flex", alignItems:"center", gap:24, marginBottom:20, padding:"20px 24px",
        background:T.card, borderRadius:12, border:`1px solid ${T.border}` }}>
        <ScoreRing score={score} total={QUESTIONS.length} color={score>=6?T.green:score>=4?T.amber:T.red}/>
        <div>
          <div style={{ fontFamily:"Georgia,serif", fontSize:20, color:T.text, fontWeight:700 }}>{score}/{QUESTIONS.length} Correct</div>
          <div style={{ fontSize:13, color:T.muted, marginTop:4 }}>
            {score>=6?"Excellent — RSE Element 9 mastered!":score>=4?"Good — review flagged areas":"Focus on MWRR/TWRR and communication rules"}
          </div>
          <button onClick={()=>{ setAnswers({}); setSubmitted(false); setCurrent(0); }}
            style={{ marginTop:10, padding:"6px 16px", background:T.blue.text, color:"#fff", border:"none",
              borderRadius:6, cursor:"pointer", fontSize:12, fontWeight:700 }}>Retake</button>
        </div>
      </div>
      {QUESTIONS.map((q,i)=>{
        const ua=answers[i]; const correct=ua===q.ans;
        return (
          <div key={i} style={{ marginBottom:12, padding:"14px 16px", borderRadius:10,
            background:correct?T.green.bg:T.red.bg, border:`1.5px solid ${correct?T.green.border:T.red.border}` }}>
            <div style={{ display:"flex", gap:8, alignItems:"flex-start" }}>
              <span style={{ fontSize:16 }}>{correct?"✓":"✗"}</span>
              <div style={{ flex:1 }}>
                <p style={{ margin:"0 0 6px", fontSize:13, fontWeight:700, color:T.text }}>{q.q}</p>
                {!correct&&ua!==undefined&&<p style={{ margin:"0 0 4px", fontSize:12, color:T.red.text }}>Your answer: {q.opts[ua]}</p>}
                <p style={{ margin:"0 0 4px", fontSize:12, color:T.green.text, fontWeight:600 }}>Correct: {q.opts[q.ans]}</p>
                <p style={{ margin:0, fontSize:12, color:T.muted, fontStyle:"italic" }}>{q.explain}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
  const q = QUESTIONS[current];
  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
        <Tag color={T.blue}>Q {current+1} of {QUESTIONS.length}</Tag>
        <span style={{ fontSize:12, color:T.muted }}>{Object.keys(answers).length} answered</span>
      </div>
      <div style={{ background:T.blue.bg, border:`1px solid ${T.blue.border}`, borderRadius:10, padding:"16px 18px", marginBottom:14 }}>
        <p style={{ margin:0, fontSize:14, lineHeight:1.75, color:T.text, fontFamily:"Georgia,serif" }}>{q.q}</p>
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:16 }}>
        {q.opts.map((opt,ai)=>{
          const selected=answers[current]===ai;
          return (
            <button key={ai} onClick={()=>!submitted&&setAnswers(p=>({...p,[current]:ai}))}
              style={{ padding:"12px 16px", borderRadius:8, border:`1.5px solid ${selected?T.blue.text:T.border}`,
                background:selected?T.blue.bg:T.card, cursor:"pointer", textAlign:"left",
                fontSize:13, color:T.text, transition:"all 0.15s", lineHeight:1.5 }}>
              <span style={{ fontWeight:700, color:T.blue.text, marginRight:6 }}>{["A","B","C","D"][ai]}.</span>{opt}
            </button>
          );
        })}
      </div>
      <div style={{ display:"flex", gap:10 }}>
        {current>0&&<button onClick={()=>setCurrent(c=>c-1)} style={{ padding:"9px 18px", borderRadius:8,
          border:`1px solid ${T.border}`, background:T.card, cursor:"pointer", fontSize:13, color:T.muted }}>← Prev</button>}
        {current<QUESTIONS.length-1
          ?<button onClick={()=>setCurrent(c=>c+1)} style={{ padding:"9px 22px", borderRadius:8,
              background:T.blue.text, color:"#fff", border:"none", cursor:"pointer", fontSize:13, fontWeight:700 }}>Next →</button>
          :<button onClick={()=>setSubmitted(true)} disabled={Object.keys(answers).length<QUESTIONS.length}
              style={{ padding:"9px 22px", borderRadius:8, fontWeight:700, fontSize:13, border:"none", color:"#fff", cursor:Object.keys(answers).length<QUESTIONS.length?"not-allowed":"pointer",
                background:Object.keys(answers).length<QUESTIONS.length?T.dim:T.green.text }}>Submit Quiz ✓</button>}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   QUICK REFERENCE
══════════════════════════════════════════════════════════════════════════════ */
const QUICK_REF = [
  { topic:"Monitoring Duty", detail:"Ongoing obligation to ensure portfolio remains suitable as BOTH client (KYC) and market (KYP) change over time. Not 'one-and-done.'" },
  { topic:"External Triggers", detail:"Economy cycle shift | Interest rate change (rising rates → long bonds too risky) | Fund underperforms benchmark for multiple periods" },
  { topic:"Internal Triggers", detail:"Major life event (retirement, job loss, illness) | Portfolio drift from target allocation | Client panic at minor dip (reveals true risk tolerance)" },
  { topic:"Annual Review Purpose", detail:"Proactively RE-DISCOVER the client's KYC each year. Confirm: personal life, income, health, goals, time horizons, and risk tolerance are all unchanged." },
  { topic:"MWRR", detail:"Client's PERSONAL return. Appears on account statement. Distorted by timing/size of client's own deposits and withdrawals. Not for comparing funds." },
  { topic:"TWRR", detail:"Manager's OFFICIAL return. Appears in Fund Facts. Removes all client cash flow effects. MUST use TWRR to compare funds or evaluate manager skill." },
  { topic:"Alpha Formula", detail:"Alpha = Fund TWRR − Benchmark Return. Negative alpha = client paying high MER to underperform the 'dumb' index. Triggers sell recommendation." },
  { topic:"SAMURAI Benchmark Rules", detail:"Specified in advance | Appropriate (apples-to-apples) | Measurable | Unambiguous | Reflective (same style) | Accountable | Investable" },
  { topic:"Peer Group Analysis", detail:"Compares fund to 'class average' of all funds with same objective. 1st Quartile = top 25%. 4th Quartile = bottom 25% (often closed). Use with benchmarks for full picture." },
  { topic:"Sharpe Ratio", detail:"(Return − Risk-Free Rate) ÷ Standard Deviation. Return per unit of TOTAL risk. Higher = better. Best all-around efficiency metric. Works for any portfolio." },
  { topic:"Treynor Ratio", detail:"(Return − Risk-Free Rate) ÷ Beta. Return per unit of MARKET risk. Only valid for well-DIVERSIFIED portfolios. USELESS for single stocks." },
  { topic:"Jensen's Alpha", detail:"Actual Return − Expected Return (CAPM). True skill adjusted for Beta risk. A Beta 1.2 fund SHOULD earn more — Jensen's shows if it earned MORE than expected." },
  { topic:"Standard Deviation", detail:"Total risk / 'bumpiness.' Basis for Fund Facts risk ratings. Use for: general volatility comparison. Higher SD = more volatile = higher risk rating." },
  { topic:"Maximum Drawdown", detail:"Worst peak-to-trough historical loss. Use when client specifically asks 'what's the worst crash I'd survive?' — answers the emotional/worst-case risk question." },
  { topic:"Multi-Factor Regression", detail:"Deconstructs return into: Beta + Factor tilts (Value/Small-Cap) + True Alpha. Detects 'fake alpha' — apparent outperformance due to factor luck, not skill." },
  { topic:"CIRO Communication: 3 Core Rules", detail:"(1) Fair & Balanced (equal prominence for risks AND benefits). (2) Not Misleading (no lies, exaggerations, or material omissions). (3) Clear — plain language for the specific client." },
  { topic:"Professional Titles", detail:"Must be accurate, pre-approved by Compliance, not exaggerated. Cannot imply credentials not earned (e.g., calling yourself 'CFP' before passing exams + work experience)." },
  { topic:"Social Media: 4 Rules", detail:"(1) Compliance pre-approval. (2) No hype or guarantees. (3) No public recommendations (zero KYC). (4) No client testimonials — even 'liking' one is a violation." },
  { topic:"Off-Channel Communication", detail:"NEVER discuss client business on WhatsApp, iMessage, personal email. These bypass supervision and destroy the official record. Regulator assumes guilt if records are missing." },
  { topic:"Record-Keeping: Golden Rule", detail:"'If it wasn't written down, it didn't happen.' Need: KYC records, Account Appropriateness, Suitability Determination (Master Note), KYP (firm's), COI (conflicts + alternatives considered)." },
];

function QuickRef() {
  return (
    <div style={{ overflowX:"auto" }}>
      <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
        <thead>
          <tr style={{ background:T.blue.bg }}>
            {["Topic","Key Detail"].map(h=>(
              <th key={h} style={{ padding:"10px 12px", textAlign:"left", color:T.blue.text, fontWeight:700, borderBottom:`2px solid ${T.blue.border}` }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {QUICK_REF.map((r,i)=>(
            <tr key={i} style={{ background:i%2===0?T.bg:T.card, borderBottom:`1px solid ${T.border}` }}>
              <td style={{ padding:"9px 12px", fontWeight:700, color:T.text, whiteSpace:"nowrap" }}>{r.topic}</td>
              <td style={{ padding:"9px 12px", color:T.muted, lineHeight:1.6 }}>{r.detail}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   MAIN APP
══════════════════════════════════════════════════════════════════════════════ */
const TABS = [
  { id:"s1", label:"Monitoring Performance", icon:"🩺" },
  { id:"s2", label:"Performance Disclosure", icon:"📊" },
  { id:"s3", label:"Performance Calculations", icon:"🔬" },
  { id:"s4", label:"CIRO Communications", icon:"📡" },
  { id:"s5", label:"Client Records", icon:"📁" },
  { id:"flash", label:"Flashcards", icon:"🃏" },
  { id:"quiz", label:"Quiz", icon:"✏️" },
  { id:"ref", label:"Quick Ref", icon:"📋" },
];

export default function App() {
  const [active, setActive] = useState("s1");
  const [scrollPct, setScrollPct] = useState(0);
  useEffect(()=>{
    const onScroll=()=>{
      const el=document.documentElement;
      setScrollPct((el.scrollTop/(el.scrollHeight-el.clientHeight))*100);
    };
    window.addEventListener("scroll", onScroll);
    return ()=>window.removeEventListener("scroll", onScroll);
  },[]);
  return (
    <div style={{ fontFamily:"system-ui,-apple-system,sans-serif", background:T.bg, minHeight:"100vh", color:T.text }}>
      <div style={{ position:"fixed", top:0, left:0, right:0, height:3, background:T.border, zIndex:1000 }}>
        <div style={{ height:"100%", background:`linear-gradient(90deg,${T.blue.text},${T.teal.text})`,
          width:`${scrollPct}%`, transition:"width 0.1s" }}/>
      </div>
      <div style={{ background:T.card, borderBottom:`1px solid ${T.border}`, padding:"20px 24px 0", position:"sticky", top:3, zIndex:100 }}>
        <div style={{ maxWidth:980, margin:"0 auto" }}>
          <div style={{ display:"flex", alignItems:"baseline", gap:12, marginBottom:14 }}>
            <span style={{ fontFamily:"Georgia,serif", fontSize:22, fontWeight:700, color:T.text }}>Element 9</span>
            <span style={{ color:T.muted, fontSize:14 }}>Monitoring, Reporting & Maintaining Client Relationships</span>
            <Tag color={T.blue}>7 Exam Questions</Tag>
          </div>
          <div style={{ display:"flex", gap:2, overflowX:"auto", paddingBottom:0 }}>
            {TABS.map(t=>(
              <button key={t.id} onClick={()=>{ setActive(t.id); window.scrollTo({top:0,behavior:"smooth"}); }}
                style={{ padding:"8px 12px", border:"none", borderBottom:`3px solid ${active===t.id?T.blue.text:"transparent"}`,
                  background:"none", cursor:"pointer", fontSize:12, fontWeight:active===t.id?700:500,
                  color:active===t.id?T.blue.text:T.muted, whiteSpace:"nowrap", transition:"all 0.2s",
                  display:"flex", alignItems:"center", gap:5 }}>
                <span>{t.icon}</span><span>{t.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      <div style={{ maxWidth:980, margin:"0 auto", padding:"28px 20px 60px" }}>
        {active==="s1"&&<Section1/>}
        {active==="s2"&&<Section2/>}
        {active==="s3"&&<Section3/>}
        {active==="s4"&&<Section4/>}
        {active==="s5"&&<Section5/>}
        {active==="flash"&&<Reveal><SectionLabel color={T.teal}>Flashcards — 20 Cards</SectionLabel><Flashcards/></Reveal>}
        {active==="quiz"&&<Reveal><SectionLabel color={T.orange}>Practice Quiz — 7 Questions</SectionLabel><Quiz/></Reveal>}
        {active==="ref"&&<Reveal><SectionLabel color={T.blue}>Quick Reference — 20 Key Facts</SectionLabel><QuickRef/></Reveal>}
      </div>
    </div>
  );
}
